import React from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { FileText, ShieldCheck, AlertTriangle } from "lucide-react";

const COUNSEL_NOTE = "Placeholder text pending legal review. Final wording to be supplied by counsel before public launch.";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`legal-section-${id}`}>
      <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-foreground mb-3">{title}</h2>
      <div className="space-y-3 text-sm font-body text-secondary-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function CounselFlag() {
  return (
    <div className="border border-amber-500/40 bg-amber-500/5 px-3 py-2 flex items-start gap-2" data-testid="legal-counsel-flag">
      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
      <span className="font-mono text-[11px] uppercase text-amber-400">{COUNSEL_NOTE}</span>
    </div>
  );
}

export default function Legal() {
  const { pathname } = useLocation();
  const isPrivacy = pathname.includes("privacy");
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-[820px] mx-auto px-4 py-10" data-testid="legal-page">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-6 h-6 text-olive-bright" />
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">{isPrivacy ? "Privacy Policy" : "Terms of Service"}</h1>
        </div>
        <p className="font-mono text-[11px] uppercase text-muted-foreground mb-6">OpticSwap · Draft · pending legal review</p>

        <div className="space-y-10">
          {!isPrivacy && (
            <>
              <Section id="terms" title="Terms of Service">
                <CounselFlag />
                <p>These Terms govern your use of the OpticSwap marketplace for buying, selling, and trading optics. By using OpticSwap you agree to conduct all transactions on-platform and to comply with all applicable laws.</p>
              </Section>
              <Section id="messages" title="Messages, Retention & Staff Access">
                <p className="text-foreground">The following describes how member-to-member messages are handled. This is the operative summary; the full legal wording is pending counsel.</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><span className="text-foreground">Messages are retained.</span> Conversations between members are kept for up to seven (7) years. "Deleting" a conversation hides it from your own view; the record persists so that disputes, chargebacks, and legal claims can be resolved fairly.</li>
                  <li><span className="text-foreground">Transaction linkage.</span> Messages tied to an order are associated with that order for the life of the retention window.</li>
                  <li><span className="text-foreground">Staff review is dispute-scoped.</span> OpticSwap does not browse member inboxes. Staff may review a specific conversation only to resolve a dispute or a filed report relating to that conversation, and only from that case.</li>
                  <li><span className="text-foreground">Support may join a conversation.</span> When staff join a conversation to help, both members are shown a system notice and every staff message is clearly labelled "OpticSwap Support." Staff never post as a member.</li>
                  <li><span className="text-foreground">Every access is logged.</span> Each time staff open, join, post in, or leave a conversation, an audit record is written (who, when, which conversation, which case).</li>
                </ul>
                <CounselFlag />
              </Section>
            </>
          )}
          {isPrivacy && (
            <>
              <Section id="privacy" title="Privacy Policy">
                <CounselFlag />
                <p>This Policy explains what information OpticSwap collects and how it is used. We collect the information you provide to operate the marketplace and to keep transactions safe.</p>
              </Section>
              <Section id="messages" title="Message Retention & Access">
                <div className="flex items-center gap-2 text-teal-300 mb-1"><ShieldCheck className="w-4 h-4" /><span className="font-mono text-[11px] uppercase">How we handle your messages</span></div>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>We retain member-to-member messages for up to seven (7) years to support dispute resolution, chargebacks, and legal or regulatory requests.</li>
                  <li>Deleting a conversation removes it from your view only; the underlying record is retained for the period above.</li>
                  <li>Staff access to your messages is limited to resolving a specific dispute or report and is initiated only from that case — there is no general staff inbox of member messages.</li>
                  <li>When Support joins a conversation, you will see a notice in the thread, and Support messages are always labelled as such.</li>
                  <li>Every staff read, join, message, and leave is recorded in an internal audit log.</li>
                </ul>
                <CounselFlag />
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
